import { useMemo } from "react";
import ServiceClient from "../ServiceClient";
import ServiceTransaction from "../ServiceTransaction";
import { useServiceClient } from "../ServiceClientContext";
import { RequestOptions } from "../ServiceRequest";

interface Node {
  id: number;
  name: string;
}

interface NodeText {
  id: number;
  nodeId: number;
  languageCode: string;
  countryCode: string;
  script: string;
  text: string;
}

interface NodeSpeech {
  id: number;
  nodeTextId: number;
  languageCode: string;
  countryCode: string;
  voice: string;
  //recording(Url): string;
}

interface ContextNode {
  id: number;
  name: string;
  sourceNodeText?: NodeText;
  targetNodeText?: NodeText;
  sourceNodeSpeech?: NodeSpeech;
  targetNodeSpeech?: NodeSpeech;
  bookmarkedAt?: string;
}

interface UserNodeSpeeches {
  nodeSpeechId: number;
  nodeId: number;
  sourceText: string;
  locale: string;
  text: string;
  voice: string;
  timesPlayed: number;
  lastPlayed?: string;
  bookmarkedAt?: string;
  playRank: number;
}

interface GetNodesOutput {
  nodes: ContextNode[];
}

interface GetNodeTextsByNodeIdOutput {
  texts: NodeText[];
}

interface RecordUserPlaysSpeechOutput {
  timesPlayed: number;
}

interface BookmarkSpeechNodeOutput {
  success: boolean;
}

interface GetUserNodeSpeechesOutput {
  speeches: UserNodeSpeeches[];
}

class NodesServiceClient {
  constructor(private serviceClient: ServiceClient) {}

  public async getNodes(sourceLanguage: string, targetLanguage: string, targetLocale: string, targetVoice: string): Promise<ServiceTransaction<GetNodesOutput>> {
    const endPoint: string = `/nodes?sourceLanguage=${encodeURIComponent(sourceLanguage)}&targetLanguage=${encodeURIComponent(targetLanguage)}&targetLocale=${encodeURIComponent(targetLocale)}&targetVoice=${encodeURIComponent(targetVoice)}`;
    return this.serviceClient.get(endPoint);
  }

  public async getNode(id: number): Promise<any> {
    const endPoint: string = `/nodes/${id}`;
    return this.serviceClient.get(endPoint);
  }

  public async getNodeTexts(id: number): Promise<ServiceTransaction<GetNodeTextsByNodeIdOutput>> {
    const endPoint: string = `/nodes/${id}/texts`;
    return this.serviceClient.get(endPoint);
  }

  public async postAddNode(name: string): Promise<any> {
    const endPoint: string = "/nodes";
    // Don't encode string values which are part of the request body
    let options:RequestOptions = { body: { name: name }};
    return this.serviceClient.post(endPoint, options);
  }

  public async postAddNodeText(nodeId: number, language: string): Promise<any> {
    // Encode string values which are part of the request url
    const endPoint: string = `/nodes/${nodeId}/texts/${encodeURIComponent(language)}`;
    return this.serviceClient.post(endPoint);
  }

  public async postAddNodeSpeech(nodeId: number, language: string, locale: string, voice: string): Promise<any> {
    const endPoint: string = `/nodes/${nodeId}/texts/${encodeURIComponent(language)}/${encodeURIComponent(locale)}/${encodeURIComponent(voice)}`;
    return this.serviceClient.post(endPoint);
  }

  public async getNodeSpeechBinary(nodeId: number, language: string, locale: string, voice: string): Promise<any> {
    const endPoint: string = `/nodes/${nodeId}/speech/${encodeURIComponent(locale)}/${encodeURIComponent(voice)}/wav`;
    return this.serviceClient.getBinary(endPoint);
  }

  public async putRecordUserPlaysSpeech(nodeId: number, locale: string, voice: string): Promise<ServiceTransaction<RecordUserPlaysSpeechOutput>> {
    const endPoint: string = `/nodes/${nodeId}/speech/${encodeURIComponent(locale)}/${encodeURIComponent(voice)}`;
    return this.serviceClient.put(endPoint);
  }

  public async putBookmarkSpeech(nodeId: number, locale: string, voice: string, isBookmarked: boolean): Promise<ServiceTransaction<BookmarkSpeechNodeOutput>> {
    const endPoint: string = `/nodes/${nodeId}/speech/${encodeURIComponent(locale)}/${encodeURIComponent(voice)}/bookmark?isBookmarked=${encodeURIComponent(String(isBookmarked))}`;
    return this.serviceClient.put(endPoint);
  }

  public async getMostPlayedSpeeches(locale: string): Promise<ServiceTransaction<GetUserNodeSpeechesOutput>> {
    const endPoint: string = `/nodes/speeches/mostplayed/${encodeURIComponent(locale)}`;
    return this.serviceClient.get(endPoint);
  }

  public async getRecentlyPlayedSpeeches(locale: string): Promise<ServiceTransaction<GetUserNodeSpeechesOutput>> {
    const endPoint: string = `/nodes/speeches/recentlyplayed/${encodeURIComponent(locale)}`;
    return this.serviceClient.get(endPoint);
  }

  public async getBookmarkedSpeeches(locale: string): Promise<ServiceTransaction<GetUserNodeSpeechesOutput>> {
    const endPoint: string = `/nodes/speeches/bookmarked/${encodeURIComponent(locale)}`;
    return this.serviceClient.get(endPoint);
  }
}

export type {
  Node,
  NodeText,
  ContextNode,
  UserNodeSpeeches,
  GetNodesOutput,
  GetNodeTextsByNodeIdOutput,
  RecordUserPlaysSpeechOutput,
  BookmarkSpeechNodeOutput,
  GetUserNodeSpeechesOutput
};
export default NodesServiceClient;

export function useNodesServiceClient(): NodesServiceClient {
  const serviceClient = useServiceClient();
  return useMemo(() => new NodesServiceClient(serviceClient), [serviceClient]);
}
